import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Receipt, CreditCard, Star, LogOut, TrendingDown, TrendingUp, Gift, CalendarClock } from 'lucide-react';
import { usePortalStore } from '@/stores/portal.store';
import { useNavigate, Navigate } from 'react-router-dom';
import portalApi from '@/lib/portal-api';
import { formatCurrency } from '@/lib/utils';
import { TabBar } from '@/components/ui/tab-bar';
import { EmptyState } from '@/components/ui/empty-state';

const BASE_TABS = [
  { id: 'invoices', label: 'Invoices', icon: Receipt },
  { id: 'ledger', label: 'Balance', icon: CreditCard },
  { id: 'points', label: 'Points', icon: Star },
  { id: 'giftcards', label: 'Gift Cards', icon: Gift },
] as const;

const INSTALLMENTS_TAB = { id: 'installments', label: 'Plans', icon: CalendarClock } as const;

type Tab = 'invoices' | 'ledger' | 'points' | 'giftcards' | 'installments';

// Portal API response shapes (Decimal columns serialize as number | string).
interface PortalInvoiceItem {
  quantity: number | string;
  product?: { name: string } | null;
}
interface PortalInvoice {
  id: string;
  invoiceNumber: string;
  createdAt: string;
  totalAmount: number | string;
  status: string;
  items: PortalInvoiceItem[];
}
interface PortalInvoicesResponse {
  data?: PortalInvoice[];
}

interface PortalLedgerEntry {
  id: string;
  type: string;
  notes?: string | null;
  createdAt: string;
  amount: number | string;
  balanceAfter: number | string;
}
interface PortalLedgerResponse {
  balance?: number | string | null;
  creditLimit?: number | string | null;
  entries?: PortalLedgerEntry[];
}

interface PortalLoyaltyTransaction {
  type: string;
  notes?: string | null;
  createdAt: string;
  points: number;
}
interface PortalLoyaltyResponse {
  points?: number;
  totalEarned?: number;
  totalRedeemed?: number;
  transactions?: PortalLoyaltyTransaction[];
}

interface PortalGiftCardTransaction {
  type: string;
  createdAt: string;
  amount: number | string;
}
interface PortalGiftCard {
  id: string;
  code: string;
  status: string;
  balance: number | string;
  expiresAt?: string | null;
  transactions: PortalGiftCardTransaction[];
}
interface PortalGiftCardsResponse {
  data?: PortalGiftCard[];
}

interface PortalInstallmentPlan {
  id: string;
  status: string;
  totalAmount: number | string;
  totalPaid: number | string;
  totalDue: number | string;
  numberOfInstallments: number;
  installmentAmount: number | string;
  frequency: string;
  nextDuePayment?: {
    dueDate: string;
    amount: number | string;
    paidAmount?: number | string | null;
  } | null;
}
interface PortalInstallmentsResponse {
  entitled?: boolean;
  data?: PortalInstallmentPlan[];
}

export function PortalDashboardPage() {
  const navigate = useNavigate();
  const { customer, logout, isAuthenticated } = usePortalStore();
  const [activeTab, setActiveTab] = useState<Tab>('invoices');
  const tenantId = new URLSearchParams(window.location.search).get('tenant') ?? '';

  // All hooks are declared unconditionally (rules-of-hooks, T-400). When the
  // visitor is unauthenticated the queries are gated off via `enabled`, and the
  // login redirect happens only after every hook has run.
  const { data: invoicesData, isLoading: invoicesLoading } = useQuery<PortalInvoicesResponse>({
    queryKey: ['portal-invoices', tenantId],
    queryFn: () =>
      portalApi
        .get('/portal/invoices', { headers: { 'x-tenant-id': tenantId } })
        .then((r) => r.data),
    enabled: isAuthenticated && activeTab === 'invoices',
  });

  const { data: ledgerData, isLoading: ledgerLoading } = useQuery<PortalLedgerResponse>({
    queryKey: ['portal-ledger', tenantId],
    queryFn: () =>
      portalApi
        .get('/portal/ledger', { headers: { 'x-tenant-id': tenantId } })
        .then((r) => r.data),
    enabled: isAuthenticated && activeTab === 'ledger',
  });

  const { data: loyaltyData, isLoading: loyaltyLoading } = useQuery<PortalLoyaltyResponse>({
    queryKey: ['portal-loyalty', tenantId],
    queryFn: () =>
      portalApi
        .get('/portal/loyalty', { headers: { 'x-tenant-id': tenantId } })
        .then((r) => r.data),
    enabled: isAuthenticated && activeTab === 'points',
  });

  const { data: giftCardsData, isLoading: giftCardsLoading } = useQuery<PortalGiftCardsResponse>({
    queryKey: ['portal-gift-cards', tenantId],
    queryFn: () =>
      portalApi
        .get('/portal/gift-cards', { headers: { 'x-tenant-id': tenantId } })
        .then((r) => r.data),
    enabled: isAuthenticated && activeTab === 'giftcards',
  });

  // Always-enabled probe: drives Installments tab visibility (addon-gated; the
  // endpoint returns `{ entitled: false }` when the tenant lacks the BNPL addon).
  const { data: installmentsData, isLoading: installmentsLoading } = useQuery<PortalInstallmentsResponse>({
    queryKey: ['portal-installments', tenantId],
    queryFn: () =>
      portalApi
        .get('/portal/installments', { headers: { 'x-tenant-id': tenantId } })
        .then((r) => r.data),
    enabled: isAuthenticated,
  });

  // Redirect to login if not authenticated (after all hooks have run). Declarative
  // <Navigate> instead of an in-render navigate() call — render stays side-effect-free
  // and won't double-fire under StrictMode (T-405).
  if (!isAuthenticated) {
    return <Navigate to={`/portal/login?tenant=${tenantId}`} replace />;
  }

  const handleLogout = () => {
    logout();
    navigate(`/portal/login?tenant=${tenantId}`);
  };

  const tabs = installmentsData?.entitled ? [...BASE_TABS, INSTALLMENTS_TAB] : BASE_TABS;

  return (
    <div className="min-h-screen bg-muted">
      {/* Header */}
      <div className="bg-primary text-primary-foreground px-4 pt-6 pb-16">
        <div className="flex justify-between items-start max-w-lg mx-auto">
          <div>
            <p className="text-primary-foreground text-sm">Welcome back</p>
            <h1 className="text-xl font-bold">{customer?.name}</h1>
            <p className="text-primary-foreground text-xs mt-0.5">{customer?.phone}</p>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-1 text-primary-foreground hover:text-primary-foreground text-sm min-h-[44px] px-2 -me-2"
          >
            <LogOut className="h-4 w-4" /> Sign Out
          </button>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 -mt-10">
        <TabBar
          className="mb-4 rounded-2xl bg-card p-1.5 shadow-lg"
          bordered={false}
          equal
          tabs={tabs.map(({ id, label, icon }) => ({ key: id, label, icon }))}
          activeTab={activeTab}
          onTabChange={(key) => setActiveTab(key as Tab)}
        />

        {/* Invoices Tab */}
        {activeTab === 'invoices' && (
          <div className="space-y-3">
            {invoicesLoading ? (
              <div className="text-center py-8 text-muted-foreground">Loading…</div>
            ) : invoicesData?.data?.length === 0 ? (
              <EmptyState icon={Receipt} title="No invoices yet" className="py-8" />
            ) : (
              invoicesData?.data?.map((sale: PortalInvoice) => (
                <div key={sale.id} className="bg-card rounded-xl shadow-sm p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold text-sm">{sale.invoiceNumber}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {new Date(sale.createdAt).toLocaleDateString('en-PK', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </p>
                    </div>
                    <div className="text-end">
                      <p className="font-bold text-primary-text">
                        {formatCurrency(Number(sale.totalAmount))}
                      </p>
                      <span
                        className={`text-xs rounded-full px-2 py-0.5 ${
                          sale.status === 'COMPLETED'
                            ? 'bg-success/10 text-success-text'
                            : 'bg-muted text-muted-foreground'
                        }`}
                      >
                        {sale.status}
                      </span>
                    </div>
                  </div>
                  {sale.items?.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-border">
                      {sale.items.slice(0, 2).map((item: PortalInvoiceItem, i: number) => (
                        <p key={i} className="text-xs text-muted-foreground">
                          {item.quantity}× {item.product?.name}
                        </p>
                      ))}
                      {sale.items.length > 2 && (
                        <p className="text-xs text-muted-foreground">+{sale.items.length - 2} more</p>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {/* Ledger Tab */}
        {activeTab === 'ledger' && (
          <div className="space-y-3">
            {ledgerLoading ? (
              <div className="text-center py-8 text-muted-foreground">Loading…</div>
            ) : (
              <>
                <div
                  className={`rounded-xl shadow-sm p-5 text-primary-foreground ${
                    Number(ledgerData?.balance ?? 0) > 0 ? 'bg-destructive' : 'bg-success'
                  }`}
                >
                  <p className="text-sm opacity-80">Outstanding Balance (Udhar)</p>
                  <p className="text-3xl font-bold mt-1">
                    {formatCurrency(Math.abs(Number(ledgerData?.balance ?? 0)))}
                  </p>
                  {ledgerData?.creditLimit && (
                    <p className="text-xs opacity-70 mt-1">
                      Credit Limit: {formatCurrency(Number(ledgerData.creditLimit))}
                    </p>
                  )}
                  {Number(ledgerData?.balance ?? 0) <= 0 && (
                    <p className="text-xs opacity-80 mt-1">✓ All clear</p>
                  )}
                </div>
                {ledgerData?.entries?.map((entry: PortalLedgerEntry) => (
                  <div
                    key={entry.id}
                    className="bg-card rounded-xl shadow-sm p-4 flex justify-between items-center"
                  >
                    <div className="flex items-center gap-3">
                      {entry.type === 'CHARGE' ? (
                        <TrendingUp className="h-5 w-5 text-destructive-text" />
                      ) : (
                        <TrendingDown className="h-5 w-5 text-success-text" />
                      )}
                      <div>
                        <p className="text-sm font-medium">{entry.notes ?? entry.type}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(entry.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="text-end">
                      <p
                        className={`font-semibold text-sm ${
                          entry.type === 'CHARGE' ? 'text-destructive-text' : 'text-success-text'
                        }`}
                      >
                        {entry.type === 'CHARGE' ? '+' : '-'}
                        {formatCurrency(Number(entry.amount))}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Bal: {formatCurrency(Number(entry.balanceAfter))}
                      </p>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        )}

        {/* Loyalty Points Tab */}
        {activeTab === 'points' && (
          <div className="space-y-3">
            {loyaltyLoading ? (
              <div className="text-center py-8 text-muted-foreground">Loading…</div>
            ) : (
              <>
                <div className="bg-gradient-to-br from-warning to-warning/80 rounded-xl shadow-sm p-5 text-warning-foreground">
                  <p className="text-sm opacity-80">Loyalty Points</p>
                  <p className="text-4xl font-bold mt-1">
                    {loyaltyData?.points?.toLocaleString() ?? 0}
                  </p>
                  <div className="flex gap-4 mt-2 text-xs opacity-80">
                    <span>Earned: {loyaltyData?.totalEarned?.toLocaleString() ?? 0}</span>
                    <span>Redeemed: {loyaltyData?.totalRedeemed?.toLocaleString() ?? 0}</span>
                  </div>
                </div>
                {loyaltyData?.transactions?.map((tx: PortalLoyaltyTransaction, i: number) => (
                  <div
                    key={i}
                    className="bg-card rounded-xl shadow-sm p-4 flex justify-between items-center"
                  >
                    <div>
                      <p className="text-sm font-medium">{tx.notes ?? tx.type}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(tx.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    <span
                      className={`text-sm font-bold ${
                        tx.points > 0 ? 'text-success-text' : 'text-destructive-text'
                      }`}
                    >
                      {tx.points > 0 ? '+' : ''}
                      {tx.points} pts
                    </span>
                  </div>
                ))}
                {(!loyaltyData?.transactions || loyaltyData.transactions.length === 0) && (
                  <div className="text-center py-4 text-muted-foreground text-sm">
                    No loyalty transactions yet
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Gift Cards Tab */}
        {activeTab === 'giftcards' && (
          <div className="space-y-3">
            {giftCardsLoading ? (
              <div className="text-center py-8 text-muted-foreground">Loading…</div>
            ) : !giftCardsData?.data?.length ? (
              <EmptyState icon={Gift} title="No gift cards found" className="py-8" />
            ) : (
              giftCardsData.data.map((gc: PortalGiftCard) => (
                <div key={gc.id} className="bg-card rounded-xl shadow-sm overflow-hidden">
                  <div className="bg-gradient-to-r from-primary to-primary/80 p-4 text-primary-foreground">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-xs opacity-80 mb-1">Gift Card</p>
                        <p className="font-mono text-lg font-bold tracking-widest">{gc.code}</p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        gc.status === 'ACTIVE' ? 'bg-card/20 text-primary-foreground' :
                        gc.status === 'VOID' ? 'bg-destructive/20 text-destructive-foreground' : 'bg-muted/20 text-foreground'
                      }`}>
                        {gc.status}
                      </span>
                    </div>
                    <div className="mt-3 flex items-end justify-between">
                      <div>
                        <p className="text-xs opacity-70">Balance</p>
                        <p className="text-2xl font-bold">{formatCurrency(Number(gc.balance))}</p>
                      </div>
                      {gc.expiresAt && (
                        <p className="text-xs opacity-70">
                          Expires {new Date(gc.expiresAt).toLocaleDateString('en-PK', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </p>
                      )}
                    </div>
                  </div>
                  {gc.transactions?.length > 0 && (
                    <div className="p-3 space-y-2">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Recent Activity</p>
                      {gc.transactions.slice(0, 3).map((tx: PortalGiftCardTransaction, i: number) => (
                        <div key={i} className="flex justify-between items-center text-sm">
                          <div>
                            <p className="text-xs font-medium">{tx.type}</p>
                            <p className="text-xs text-muted-foreground">{new Date(tx.createdAt).toLocaleDateString()}</p>
                          </div>
                          <span className={`text-xs font-bold ${Number(tx.amount) > 0 ? 'text-success-text' : 'text-destructive-text'}`}>
                            {Number(tx.amount) > 0 ? '+' : ''}{formatCurrency(Math.abs(Number(tx.amount)))}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {/* Installments Tab (BNPL addon-gated) */}
        {activeTab === 'installments' && (
          <div className="space-y-3">
            {installmentsLoading ? (
              <div className="text-center py-8 text-muted-foreground">Loading…</div>
            ) : !installmentsData?.data?.length ? (
              <EmptyState icon={CalendarClock} title="No installment plans" className="py-8" />
            ) : (
              installmentsData.data.map((plan: PortalInstallmentPlan) => {
                const progress =
                  Number(plan.totalAmount) > 0
                    ? Math.min(100, Math.round((Number(plan.totalPaid) / Number(plan.totalAmount)) * 100))
                    : 0;
                return (
                  <div key={plan.id} className="bg-card rounded-xl shadow-sm overflow-hidden">
                    <div className="bg-gradient-to-r from-success to-success/80 p-4 text-success-foreground">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-xs opacity-80 mb-1">Installment Plan</p>
                          <p className="text-2xl font-bold">{formatCurrency(Number(plan.totalAmount))}</p>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          plan.status === 'COMPLETED' ? 'bg-card/20 text-primary-foreground' :
                          plan.status === 'DEFAULTED' ? 'bg-destructive/20 text-destructive-foreground' : 'bg-card/20 text-destructive-foreground'
                        }`}>
                          {plan.status}
                        </span>
                      </div>
                      <div className="mt-3">
                        <div className="flex justify-between text-xs opacity-80 mb-1">
                          <span>Paid {formatCurrency(Number(plan.totalPaid))}</span>
                          <span>Due {formatCurrency(Number(plan.totalDue))}</span>
                        </div>
                        <div className="h-2 rounded-full bg-card/25 overflow-hidden">
                          <div className="h-full bg-card rounded-full" style={{ width: `${progress}%` }} />
                        </div>
                      </div>
                    </div>
                    <div className="p-3 space-y-2">
                      {plan.nextDuePayment ? (
                        <div className="flex justify-between items-center rounded-lg bg-warning/10 px-3 py-2">
                          <div>
                            <p className="text-xs font-semibold text-warning-text">Next Due</p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(plan.nextDuePayment.dueDate).toLocaleDateString('en-PK', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </p>
                          </div>
                          <span className="text-sm font-bold text-warning-text">
                            {formatCurrency(Number(plan.nextDuePayment.amount) - Number(plan.nextDuePayment.paidAmount ?? 0))}
                          </span>
                        </div>
                      ) : (
                        <p className="text-center text-xs text-success-text py-1">✓ Fully paid</p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        {plan.numberOfInstallments} × {formatCurrency(Number(plan.installmentAmount))} · {plan.frequency?.toLowerCase?.() ?? plan.frequency}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        <div className="h-8" />
      </div>
    </div>
  );
}
